// Order sets R1, AC2 (PROD-2721; rules in PROD-2673 comment 47782): when is an order-set item
// ALREADY ordered on this visit? Pure functions only, so every rule is testable without a page.
//
// Scope: the CURRENT VISIT only — the orders on the canvas (plus the ones this application has
// already placed), never a look-back over earlier visits. A duplicate is not added; the apply lists
// it beside the existing order with "Order anyway" (usage event `order_anyway`).
//
//   Lab        duplicate: same test code, or every member of the candidate panel is already ordered.
//              not:       collection timing differs explicitly (serial or fasting). A partly
//                         overlapping panel IS added; its shared members are drawn once by the
//                         PROD-2640 P-B7 logic (`alreadyOrderedTests`), not here.
//   Imaging    duplicate: same modality, body site and laterality.
//              not:       different laterality or views.
//   Medication duplicate: same DDC code (R1). Therapeutic duplication has no source yet.
//   Vaccine    duplicate: same vaccine code and same dose number.
//              not:       the next dose in a series.
//   Procedure  duplicate: same CPT code.        not: a different laterality modifier.
//   Education  duplicate: the same article. The A/P holds one copy of an article, so a duplicate
//              article is simply not attached again (there is nothing to "order anyway").
//
// HOW THE QUALIFIERS ARE READ. The A&P payload has no structured field for collection timing,
// laterality, views or dose number (see `CodedOrderPayload` and the wire types in assessment-api),
// so they are read from the order's own words — its display and note. The imaging CODE already
// carries modality and body site (and, for CPT radiology, the number of views), so "same imaging
// study" is the same code, then laterality and views from the text.
//
// WHEN A QUALIFIER IS UNKNOWN the order stays a duplicate. A false "duplicate" costs one click
// ("Order anyway"); a false "not a duplicate" orders the same study twice. The one asymmetry is
// collection timing: an unmarked lab is a routine draw, which is itself a timing, so "fasting"
// against an unmarked draw counts as an explicit difference.
//
// CODES are compared trimmed and case-insensitively, and with their system when BOTH sides carry
// one (systems are aliased, so "http://loinc.org" and "LOINC" are the same system). A blank code
// never matches.
import type { CodedOrderPayload, OrderCoding } from '../lib/order-payload';
import type { EducationArticle } from '../lib/to-education-article';

/** An order already on the visit, as the duplicate rules read it. */
export interface ExistingOrder {
  /** The order's row label, shown beside the duplicate. */
  label: string;
  /** Its structured payload (undefined for a row the canvas holds without one: never a match). */
  payload: CodedOrderPayload | undefined;
}

/** A coding as the rules compare it. */
export interface CodeKey {
  system?: string | null;
  code?: string | null;
}

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

const SYSTEM_ALIASES: Array<[RegExp, string]> = [
  [/loinc/, 'loinc'],
  [/\bcpt\b|ama-assn/, 'cpt'],
  [/\bcvx\b/, 'cvx'],
  [/\bddc\b/, 'ddc'],
  [/snomed|sct\b/, 'snomed'],
  [/rxnorm/, 'rxnorm'],
  [/icd-?10/, 'icd-10'],
];

/** A coding system in one spelling: "http://loinc.org", "LOINC " and "loinc" are all `loinc`. */
export function normalizeSystem(system: string | null | undefined): string {
  const s = norm(system);
  if (!s) return '';
  for (const [re, canonical] of SYSTEM_ALIASES) if (re.test(s)) return canonical;
  return s;
}

/**
 * Same code: equal codes (trimmed, case-insensitive), and equal systems when both sides have one.
 * A blank code on either side never matches.
 */
export function sameCode(a: CodeKey | null | undefined, b: CodeKey | null | undefined): boolean {
  const code = norm(a?.code);
  if (!code || code !== norm(b?.code)) return false;
  const sa = normalizeSystem(a?.system);
  const sb = normalizeSystem(b?.system);
  return !sa || !sb || sa === sb;
}

const keyOf = (c: OrderCoding | null | undefined): CodeKey => ({ code: c?.code, system: c?.system });

// ── Qualifiers read from the order's words ──────────────────────────────────────────────────────

const textOf = (...parts: Array<string | null | undefined>) => parts.filter(Boolean).join(' ').toLowerCase();

export type CollectionTiming = 'serial' | 'fasting' | 'non-fasting' | null;

/** "Troponin I, serial" → serial; "Glucose, fasting" → fasting; "random" / "non-fasting" → non-fasting. */
export function collectionTimingOf(text: string): CollectionTiming {
  const t = text.toLowerCase();
  if (/\bserial\b/.test(t)) return 'serial';
  if (/\bnon[-\s]?fasting\b|\brandom\b/.test(t)) return 'non-fasting';
  if (/\bfasting\b/.test(t)) return 'fasting';
  return null;
}

export type Laterality = 'left' | 'right' | 'bilateral' | null;

/** "X-ray knee, left" → left; "RT" / "right" → right; "bilateral" / "both" / modifier 50 → bilateral. */
export function lateralityOf(text: string): Laterality {
  const t = text.toLowerCase();
  if (/\bbilateral\b|\bbilat\b|\bboth\b|modifier\s*-?50\b|\d-50\b/.test(t)) return 'bilateral';
  const left = /\bleft\b|\blt\b/.test(t);
  const right = /\bright\b|\brt\b/.test(t);
  if (left && right) return 'bilateral';
  if (left) return 'left';
  if (right) return 'right';
  return null;
}

/** "X-ray chest, 2 views" → "2"; "single view" → "1"; "3 or more views" → "3+". */
export function viewsOf(text: string): string | null {
  const t = text.toLowerCase();
  const more = /(\d+)\s*(?:or more|\+)\s*views?\b/.exec(t);
  if (more) return `${more[1]}+`;
  const n = /(\d+)\s*views?\b/.exec(t);
  if (n) return n[1];
  if (/\bsingle view\b|\bone view\b/.test(t)) return '1';
  return null;
}

/** "Hepatitis B vaccine, dose 2" / "2nd dose" → 2. */
export function doseNumberFromText(text: string): number | null {
  const t = text.toLowerCase();
  const m = /\bdose\s*#?\s*(\d+)(?![.\d])/.exec(t) ?? /\b(\d+)(?:st|nd|rd|th)\s+dose\b/.exec(t);
  const n = m ? Number(m[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Both sides say something, and they say different things. Unknown on either side = not different. */
const bothKnownAndDiffer = <T>(a: T | null, b: T | null) => a !== null && b !== null && a !== b;

// ── Per type ────────────────────────────────────────────────────────────────────────────────────

type Lab = Extract<CodedOrderPayload, { kind: 'lab' }>;
type Imaging = Extract<CodedOrderPayload, { kind: 'imaging' }>;
type Procedure = Extract<CodedOrderPayload, { kind: 'procedure' }>;
type Vaccination = Extract<CodedOrderPayload, { kind: 'vaccination' }>;

const labTiming = (p: Lab) => collectionTimingOf(textOf(p.code.displayName, p.note));

/** Lab timings differ explicitly: one side is marked and the marks are not the same. */
export function timingDiffers(a: CollectionTiming, b: CollectionTiming): boolean {
  return (a !== null || b !== null) && a !== b;
}

/** The tests a lab order draws: its selected panel members, or the test itself when it has none. */
function labMembers(p: Lab): CodeKey[] {
  return p.subTests.length > 0 ? p.subTests.map(keyOf) : [keyOf(p.code)];
}

function matchLab(candidate: Lab, existing: ExistingOrder[]): string[] {
  const timing = labTiming(candidate);
  const labs = existing.filter(
    (e): e is ExistingOrder & { payload: Lab } => e.payload?.kind === 'lab' && !timingDiffers(timing, labTiming(e.payload)),
  );
  // 1) The same test code.
  const same = labs.filter((e) => sameCode(e.payload.code, candidate.code));
  if (same.length > 0) return same.map((e) => e.label);
  // 2) Every member of the candidate panel is already drawn by the visit's labs (their own code or
  //    one of their panel members). A partly covered panel is NOT a duplicate.
  const members = labMembers(candidate);
  const covering = new Set<string>();
  for (const m of members) {
    const by = labs.filter((e) => sameCode(e.payload.code, m) || e.payload.subTests.some((s) => sameCode(s, m)));
    if (by.length === 0) return [];
    for (const e of by) covering.add(e.label);
  }
  return [...covering];
}

function matchImaging(candidate: Imaging, existing: ExistingOrder[]): string[] {
  const text = textOf(candidate.code.displayName, candidate.note);
  return existing
    .filter((e): e is ExistingOrder & { payload: Imaging } => e.payload?.kind === 'imaging')
    .filter((e) => sameCode(e.payload.code, candidate.code))
    .filter((e) => {
      const other = textOf(e.payload.code.displayName, e.payload.note);
      return !bothKnownAndDiffer(lateralityOf(text), lateralityOf(other)) && !bothKnownAndDiffer(viewsOf(text), viewsOf(other));
    })
    .map((e) => e.label);
}

const procedureKey = (p: Procedure): CodeKey => ({ code: p.code.code, system: p.code.system || p.system });

function matchProcedure(candidate: Procedure, existing: ExistingOrder[]): string[] {
  const side = lateralityOf(textOf(candidate.code.displayName, candidate.note));
  return existing
    .filter((e): e is ExistingOrder & { payload: Procedure } => e.payload?.kind === 'procedure')
    .filter((e) => sameCode(procedureKey(e.payload), procedureKey(candidate)))
    .filter((e) => !bothKnownAndDiffer(side, lateralityOf(textOf(e.payload.code.displayName, e.payload.note))))
    .map((e) => e.label);
}

/** The dose number of a vaccination: a structured field when the entry carries one, else its words. */
export function vaccineDoseNumber(p: Vaccination): number | null {
  const entry = p.entry as unknown as Record<string, unknown>;
  for (const k of ['doseNumber', 'doseNumberPositiveInt', 'seriesDoseNumber']) {
    const v = entry[k];
    const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v) : NaN;
    if (Number.isFinite(n) && n > 0) return n;
  }
  return doseNumberFromText(textOf(p.entry.vaccine?.display, p.entry.remarks));
}

function matchVaccination(candidate: Vaccination, existing: ExistingOrder[]): string[] {
  const dose = vaccineDoseNumber(candidate);
  return existing
    .filter((e): e is ExistingOrder & { payload: Vaccination } => e.payload?.kind === 'vaccination')
    .filter((e) => sameCode(e.payload.entry.vaccine, candidate.entry.vaccine))
    .filter((e) => !bothKnownAndDiffer(dose, vaccineDoseNumber(e.payload)))
    .map((e) => e.label);
}

/**
 * The orders on the visit that `candidate` duplicates (their labels, in visit order). Empty = not a
 * duplicate: add it. Only orders of the same type can match; a referral never does.
 */
export function findDuplicateOrders(candidate: CodedOrderPayload, existing: ExistingOrder[]): string[] {
  switch (candidate.kind) {
    case 'lab':
      return matchLab(candidate, existing);
    case 'imaging':
      return matchImaging(candidate, existing);
    case 'procedure':
      return matchProcedure(candidate, existing);
    case 'vaccination':
      return matchVaccination(candidate, existing);
    case 'medication':
      return existing
        .filter((e) => e.payload?.kind === 'medication' && sameCode(e.payload.medicationCode, candidate.medicationCode))
        .map((e) => e.label);
    case 'referral':
      return [];
  }
}

// ── Education ───────────────────────────────────────────────────────────────────────────────────

const normUrl = (u: string | undefined) => norm(u).replace(/^https?:\/\//, '').replace(/\/+$/, '');

/**
 * The same article: the same title (the A/P holds one article per title, `dedupeArticles`), or the
 * same link under another title.
 */
export function sameArticle(a: EducationArticle, b: EducationArticle): boolean {
  const da = norm(a.display);
  if (da && da === norm(b.display)) return true;
  const ua = normUrl(a.articleUrl);
  return !!ua && ua === normUrl(b.articleUrl);
}
